/**
 * Explorer preference helpers — field mapping matches Profile.tsx / CVUpload.tsx
 * (`loadProfile` / `loadProfileSettings`): GET /profiles/{id}, then `|| ''` / `?? false`.
 */

export interface MappedExplorerPrefs {
  career_status: string;
  base_locations: string;
  preferred_locations: string;
  role_preferences: string;
  current_role: string;
  current_level: string;
  is_contactable: boolean;
  cv_id: string | null;
  is_recruiter: boolean;
}

/** Same normalization as Profile.tsx setFormData / CVUpload preloadedData. */
export function mapExplorerPrefsFromApi(data: {
  career_status?: string | null;
  base_locations?: string | null;
  preferred_locations?: string | null;
  role_preferences?: string | null;
  current_role?: string | null;
  current_level?: string | null;
  is_contactable?: boolean;
  cv_id?: string | null;
  is_recruiter?: boolean;
}): MappedExplorerPrefs {
  return {
    career_status: data.career_status || 'invisible',
    base_locations: data.base_locations || '',
    preferred_locations: data.preferred_locations || '',
    role_preferences: data.role_preferences || '',
    current_role: data.current_role || '',
    current_level: data.current_level || '',
    is_contactable: data.is_contactable ?? false,
    cv_id: data.cv_id || null,
    is_recruiter: !!data.is_recruiter,
  };
}

export type ExplorerOptInGap =
  | 'contactable'
  | 'recruiter_visibility'
  | 'base_locations'
  | 'preferred_locations'
  | 'role_preferences'
  | 'current_role'
  | 'current_level';

/** Which Passport fields the explorer still needs to review or fill in. */
export function getExplorerOptInGaps(data: Parameters<typeof mapExplorerPrefsFromApi>[0]): ExplorerOptInGap[] {
  const p = mapExplorerPrefsFromApi(data);
  const gaps: ExplorerOptInGap[] = [];
  if (!p.is_contactable) gaps.push('contactable');
  if (p.career_status === 'invisible') gaps.push('recruiter_visibility');
  if (!p.base_locations.trim()) gaps.push('base_locations');
  if (!p.preferred_locations.trim()) gaps.push('preferred_locations');
  if (!p.role_preferences.trim()) gaps.push('role_preferences');
  if (!p.current_role.trim()) gaps.push('current_role');
  if (!p.current_level.trim()) gaps.push('current_level');
  return gaps;
}

/** True when an explorer should see the one-time Passport opt-in nudge. */
export function explorerNeedsContactOptInNudge(data: Parameters<typeof mapExplorerPrefsFromApi>[0]): boolean {
  const p = mapExplorerPrefsFromApi(data);
  return !p.is_recruiter && !!p.cv_id && getExplorerOptInGaps(data).length > 0;
}

const PREFS_GAPS: ExplorerOptInGap[] = [
  'recruiter_visibility',
  'base_locations',
  'preferred_locations',
  'role_preferences',
  'current_role',
  'current_level',
];

/** PUT payload keys for gaps — one request, same as Profile.tsx partial update. */
export function buildGapSavePayload(
  gaps: ExplorerOptInGap[],
  form: MappedExplorerPrefs,
): Record<string, string | boolean> {
  const payload: Record<string, string | boolean> = {};
  if (gaps.includes('contactable')) payload.is_contactable = form.is_contactable;
  if (gaps.includes('recruiter_visibility')) payload.career_status = form.career_status;
  if (gaps.includes('base_locations')) payload.base_locations = form.base_locations;
  if (gaps.includes('preferred_locations')) payload.preferred_locations = form.preferred_locations;
  if (gaps.includes('role_preferences')) payload.role_preferences = form.role_preferences;
  if (gaps.includes('current_role')) payload.current_role = form.current_role;
  if (gaps.includes('current_level')) payload.current_level = form.current_level;
  return payload;
}

/** Deep-link target for optional "Open in Passport" link. */
export function getPassportNudgeHref(gaps: ExplorerOptInGap[]): string {
  const needsPrefs = gaps.some((g) => PREFS_GAPS.includes(g));
  const needsContactable = gaps.includes('contactable');
  if (needsPrefs) return '/?tab=passport&pp=prefs';
  if (needsContactable) return '/?tab=passport&pp=realtalk';
  return '/?tab=passport&pp=prefs';
}
